import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarCheck,
  faCheck,
  faCrown,
  faHandHoldingDollar,
  faHandshake,
  faHandshakeAngle,
  faUsersViewfinder,
  faWandMagicSparkles,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { PartnerPaywallVariant } from "partners/partnerExperiences";
import "./Paywall.scss";

type Props = {
  variant: PartnerPaywallVariant;
  loading: boolean;
  onCtaClick: () => void;
  ctaLabel: string;
  onClose?: () => void;
  showCloseButton?: boolean;
  desktopTrustedByText?: string;
  mobileTrustedByText?: string;
  showTerms?: boolean;
};

const PartnerPaywallContent: React.FC<Props> = ({
  variant,
  loading,
  onCtaClick,
  ctaLabel,
  onClose,
  showCloseButton = true,
  desktopTrustedByText = "Trusted by 1000+ local restaurants",
  mobileTrustedByText = "Trusted by 1000+ local businesses",
  showTerms = true,
}) => {
  if (variant !== "chowbus") {
    return null;
  }

  return (
    <div className="pw-chowbus">
      <section className="pw-left pw-chowbus__left" aria-label="Plan details">
        <div className="pw-decor pw-decor--orange" />
        <div className="pw-decor pw-decor--blue" />
        <div className="pw-left-inner pw-chowbus__left-inner">
          <div className="pw-badge pw-chowbus__badge">
            <FontAwesomeIcon icon={faCrown} />
            <span>Recommended Plan</span>
          </div>

          <h2 className="pw-title pw-chowbus__plan-title">Talk of the Town</h2>
          <p className="pw-sub pw-chowbus__plan-subtitle">
            Self-Serve Checkout Plan
          </p>

          <div className="pw-chowbus__price-wrap">
            <div className="pw-chowbus__price-row">
              <span className="pw-chowbus__price-main">FREE</span>
              <span className="pw-chowbus__price-strike">$299</span>
            </div>
            <p className="pw-chowbus__price-desc">for your first month</p>
            <p className="pw-chowbus__price-followup">
              Then $299/month after your first campaign
            </p>
          </div>

          <ul className="pw-features pw-chowbus__feature-list">
            <li className="pw-feature">
              <span className="pw-dot pw-chowbus__check">
                <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
              </span>
              <span>
                Get <strong>creator applications</strong> guaranteed
              </span>
            </li>
            <li className="pw-feature">
              <span className="pw-dot pw-chowbus__check">
                <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
              </span>
              <span>
                Reach <strong>3,000+ local customers</strong>
              </span>
            </li>
            <li className="pw-feature">
              <span className="pw-dot pw-chowbus__check">
                <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
              </span>
              <span>
                <strong>1 post + 1 story</strong> included
              </span>
            </li>
            <li className="pw-feature">
              <span className="pw-dot pw-chowbus__check">
                <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
              </span>
              <span>
                <strong>Unlimited locations</strong> — scale anytime
              </span>
            </li>
          </ul>

          <div className="pw-chowbus__desktop-cta">
            <button
              className={`pw-cta pw-chowbus__cta ${loading ? "pw-cta--loading" : ""}`}
              type="button"
              onClick={onCtaClick}
              disabled={loading}
              aria-busy={loading}
            >
              {loading ? "Processing..." : ctaLabel}
            </button>
            <p className="pw-small pw-chowbus__small-print">
              {desktopTrustedByText}
            </p>
            {showTerms ? (
              <p className="pw-terms pw-chowbus__terms">
                By subscribing, you agree to{" "}
                <a href="https://www.mustard.love/terms-of-service">
                  Mustard&apos;s Terms of Service
                </a>
              </p>
            ) : null}
          </div>
        </div>
      </section>

      <section
        className="pw-right pw-chowbus__right"
        aria-label="Offer details"
      >
        {showCloseButton && onClose ? (
          <button
            className="pw-close pw-chowbus__close"
            onClick={onClose}
            aria-label="Close"
            type="button"
          >
            <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
          </button>
        ) : null}

        <div className="pw-right-scroll pw-chowbus__right-scroll">
          <div className="pw-chowbus__offer-chip">
            <FontAwesomeIcon icon={faHandshake} />
            <span>Exclusive Partner Offer — Chowbus x Mustard</span>
          </div>

          <h3 className="pw-right-title pw-chowbus__headline">
            You&apos;ve been selected for a limited-time partnership to launch
            your first influencer campaign.
          </h3>

          <div className="pw-chowbus__highlight">
            <div className="pw-chowbus__highlight-icon">
              <FontAwesomeIcon icon={faHandshakeAngle} />
            </div>
            <p>
              <strong>In partnership with Chowbus</strong>, selected restaurants
              receive a free first month to launch their first campaign.{" "}
              <span>Limited partnership spots available this month.</span>
            </p>
          </div>

          <section className="pw-chowbus__section pw-chowbus__included-items">
            <h4 className="pw-list-title">What happens next:</h4>
            <div className="pw-chowbus__steps">
              <article>
                <div className="pw-chowbus__step-index">1</div>
                <h5>Launch in minutes</h5>
                <p>Set up your first campaign with our easy builder.</p>
              </article>
              <article>
                <div className="pw-chowbus__step-index">2</div>
                <h5>Get matched</h5>
                <p>We connect you with perfect local creators.</p>
              </article>
              <article>
                <div className="pw-chowbus__step-index">3</div>
                <h5>Receive applications</h5>
                <p>Creators apply to visit within days.</p>
              </article>
            </div>
          </section>

          <section className="pw-chowbus__section pw-chowbus__section--included">
            <div className="pw-chowbus__included-items">
              <h4 className="pw-list-title">
                Everything included in this plan
              </h4>
              <div className="pw-item pw-chowbus__included-item">
                <span className="pw-icon pw-icon--orange">
                  <FontAwesomeIcon icon={faUsersViewfinder} />
                </span>
                <div className="pw-item-body">
                  <h5>Guaranteed Applications</h5>
                  <p>
                    Invite creators and get guaranteed 3+ applications each
                    month.
                  </p>
                </div>
              </div>
              <div className="pw-item pw-chowbus__included-item">
                <span className="pw-icon pw-icon--blue">
                  <FontAwesomeIcon icon={faWandMagicSparkles} />
                </span>
                <div className="pw-item-body">
                  <h5>AI + Manual Matching</h5>
                  <p>
                    Use AI to match creators or handpick from applications —
                    your choice.
                  </p>
                </div>
              </div>
              <div className="pw-item pw-chowbus__included-item">
                <span className="pw-icon pw-icon--purple">
                  <FontAwesomeIcon icon={faCalendarCheck} />
                </span>
                <div className="pw-item-body">
                  <h5>Easy Scheduling</h5>
                  <p>Easily schedule creators to visit as often as you want.</p>
                </div>
              </div>
              <div className="pw-item pw-chowbus__included-item">
                <span className="pw-icon pw-icon--green">
                  <FontAwesomeIcon icon={faHandHoldingDollar} />
                </span>
                <div className="pw-item-body">
                  <h5>Flexible Compensation</h5>
                  <p>
                    Provide flexible compensation options, including food, cash,
                    or affiliate commissions.
                  </p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </section>

      <div className="pw-chowbus__mobile-footer">
        <button
          className={`pw-cta pw-chowbus__cta ${loading ? "pw-cta--loading" : ""}`}
          type="button"
          onClick={onCtaClick}
          disabled={loading}
          aria-busy={loading}
        >
          {loading ? "Processing..." : ctaLabel}
        </button>
        <p className="pw-small pw-chowbus__small-print">
          {mobileTrustedByText}
        </p>
      </div>
    </div>
  );
};

export default PartnerPaywallContent;
